import { computed, ref, watch, type Ref, type ComputedRef } from 'vue'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { getInvopopRegistration } from '@/api'
import { useCore } from '@/composables/core'
import { useEppo, whenEppoIsReady } from '@/composables/eppo'
import { useLogger } from '@booksy-com/vue-tools'
import { FEATURE_FLAGS } from '@/lib/consts.ts'
import { shouldDisableCustomInvoices } from '@/views/business/settings/payments/verifactu/custom-invoice-gate.ts'
import {
  normalizeRegistrationResponse,
  resolveAdapterStatusFromPayload,
} from '@/views/business/settings/payments/verifactu/registration-state.ts'

type GateCacheEntry = {
  disabled: boolean
  adapterStatus: string | null
  promise?: Promise<boolean>
}

const gateCache = new Map<string, GateCacheEntry>()

const logger = useLogger('verifactu-custom-invoice-gate')

function cacheKey (businessId: number | string): string {
  return String(businessId)
}

function isDueInvoicingFlagEnabled (value?: boolean | null): boolean {
  if (value != null) {
    return value === true
  }

  return useEppo().ff(FEATURE_FLAGS.Verifactu_due_invoicing_changes) === true
}

export function clearCustomInvoiceGateCache (businessId?: number | string): void {
  if (businessId == null) {
    gateCache.clear()
    return
  }

  gateCache.delete(cacheKey(businessId))
}

export async function resolveShouldDisableCustomInvoices (options?: {
  businessId?: number | string | null
  country?: string | null
  verifactuEnabled?: boolean | null
  dueInvoicingChangesEnabled?: boolean | null
  force?: boolean
}): Promise<boolean> {
  await whenEppoIsReady

  const { store } = useCore()
  const business = store.getters['business/business'] as {
    id?: number | string
    verifactu_enabled?: boolean
  } | null

  const businessId = options?.businessId ?? business?.id
  const country = options?.country ?? (store.getters['app/country'] as string | undefined)
  const verifactuEnabled = options?.verifactuEnabled ?? business?.verifactu_enabled
  const dueInvoicingChangesEnabled = isDueInvoicingFlagEnabled(options?.dueInvoicingChangesEnabled)

  if (
    !businessId
    || country !== 'es'
    || verifactuEnabled !== true
    || dueInvoicingChangesEnabled !== true
  ) {
    return false
  }

  const key = cacheKey(businessId)
  const cached = gateCache.get(key)

  if (!options?.force && cached && !cached.promise) {
    return cached.disabled
  }

  if (!options?.force && cached?.promise) {
    return cached.promise
  }

  const promise = (async () => {
    try {
      const { data } = await getInvopopRegistration(businessId)
      const adapterStatus = resolveAdapterStatusFromPayload(data)
        ?? normalizeRegistrationResponse(data).raw_status
      const disabled = shouldDisableCustomInvoices({
        country,
        verifactuEnabled,
        adapterStatus,
        dueInvoicingChangesEnabled: true,
      })

      gateCache.set(key, {
        disabled,
        adapterStatus,
      })

      return disabled
    } catch (error) {
      logger.warn('Failed to resolve Verifactu registration for custom invoice gate', error)
      gateCache.delete(key)
      return false
    }
  })()

  gateCache.set(key, {
    disabled: cached?.disabled ?? false,
    adapterStatus: cached?.adapterStatus ?? null,
    promise,
  })

  return promise
}

export type UseVerifactuCustomInvoiceGateReturn = {
  customInvoicesDisabled: Ref<boolean>
  customInvoicesGateReady: Ref<boolean>
  isEligibleForGate: ComputedRef<boolean>
  refresh: () => Promise<boolean>
}

export function useVerifactuCustomInvoiceGate (): UseVerifactuCustomInvoiceGateReturn {
  const { store } = useCore()

  const business = computed(() => store.getters['business/business'] as {
    id?: number | string
    verifactu_enabled?: boolean
  } | null)
  const country = computed(() => store.getters['app/country'] as string | undefined)

  const dueInvoicingChangesEnabled = ref(false)

  const customInvoicesDisabled = ref(false)
  const customInvoicesGateReady = ref(false)

  const isEligibleForGate = computed(() => (
    country.value === 'es'
    && business.value?.verifactu_enabled === true
    && dueInvoicingChangesEnabled.value
  ))

  const refresh = async (): Promise<boolean> => {
    await whenEppoIsReady
    dueInvoicingChangesEnabled.value = isDueInvoicingFlagEnabled()

    if (!isEligibleForGate.value || !business.value?.id) {
      customInvoicesDisabled.value = false
      customInvoicesGateReady.value = true
      return false
    }

    const disabled = await resolveShouldDisableCustomInvoices({
      businessId: business.value.id,
      country: country.value,
      verifactuEnabled: business.value.verifactu_enabled,
      dueInvoicingChangesEnabled: true,
      force: true,
    })

    customInvoicesDisabled.value = disabled
    customInvoicesGateReady.value = true
    return disabled
  }

  watch(
    () => [
      business.value?.id,
      business.value?.verifactu_enabled,
      country.value,
    ] as const,
    () => {
      void refresh()
    },
    { immediate: true }
  )

  return {
    customInvoicesDisabled,
    customInvoicesGateReady,
    isEligibleForGate,
    refresh,
  }
}

export async function verifactuCustomInvoiceIssuanceBeforeEnter (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const disabled = await resolveShouldDisableCustomInvoices({ force: true })

  if (disabled) {
    next({
      name: 'sales.invoices.list',
      params: {
        businessId: to.params.businessId,
      },
    })
    return
  }

  next()
}
